import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { T } from "../../../app/tokens.js";
import { getProjectMedia, uploadProjectMedia, updateProjectMedia, removeProjectMedia, restoreProjectMedia, reorderProjectMedia, setProjectCover, emitProjectMediaChanged, onProjectMediaChanged } from "../../../data/projectMediaClient.js";
import { showToast } from "../../../components/toastService.jsx";
import { MediaLeavePrompt } from "./MediaLeavePrompt.jsx";
import { MediaRemoveConfirmation } from "./MediaRemoveConfirmation.jsx";
import { proceedConfirmedNavigation } from "../recordNavigationGuard.js";
import { markGalleryDrag, endGalleryDrag, isGalleryDrag } from "./MediaReorderTile.jsx";
import { createPortal } from "react-dom";
import { MediaStrip } from "./MediaStrip.jsx";
import { MediaDrawerGrid } from "./MediaDrawerGrid.jsx";
import { MediaPreviewRow, UploadQueue } from "./MediaDrawerParts.jsx";
import { Icon } from "./mediaIcons.jsx";
import { displayCaption, resolveCover, uploadRejection, orderMedia } from "./mediaDisplay.js";
import { btn, inputStyle, muted } from "../weekly/weeklyUi.js";

const PROJECT_ADAPTER = Object.freeze({
  entityType: "project", list: (...args) => getProjectMedia(...args), upload: (...args) => uploadProjectMedia(...args),
  update: (...args) => updateProjectMedia(...args), remove: (...args) => removeProjectMedia(...args), setCover: (...args) => setProjectCover(...args),
  restore: (...args) => restoreProjectMedia(...args), reorder: (...args) => reorderProjectMedia(...args),
  emit: (...args) => emitProjectMediaChanged(...args), subscribe: (...args) => onProjectMediaChanged(...args),
});

// Personal, client-only presentation prefs for the Updates reader. Scoped by
// user + project; never written to shared project metadata.
export const MAX_MEDIA_STARS = 3;
export function mediaPrefsKey(userKey, projectId) {
  return userKey && projectId ? `places.projectImages.v1:${userKey}:${projectId}` : null;
}
function readMediaPrefs(key, photoDefault = false) {
  const fallback = { view: photoDefault ? "tile" : "list", stars: [] };
  if (!key) return fallback;
  try {
    const raw = JSON.parse(window.localStorage.getItem(key) || "null");
    if (!raw || typeof raw !== "object") return fallback;
    return {
      view: ["tile", "list"].includes(raw.view) ? raw.view : fallback.view,
      stars: Array.isArray(raw.stars) ? [...new Set(raw.stars.filter(id => typeof id === "string" || typeof id === "number"))] : [],
    };
  } catch { return fallback; }
}
function writeMediaPrefs(key, prefs) {
  if (!key) return;
  try { window.localStorage.setItem(key, JSON.stringify(prefs)); } catch { /* presentation only */ }
}

export function ProjectMediaSection(props) {
  // Key the owner so even the first render cannot expose another project's or
  // account's local preferences while a reset effect waits to run.
  return <ProjectMediaOwner key={JSON.stringify([props.mediaClient?.entityType ?? "project", props.userKey ?? null, props.ownerId ?? props.projectId])}
    {...props} projectId={props.ownerId ?? props.projectId} />;
}

function ProjectMediaOwner({ projectId, allowManage = true, weekDate, capturedItems, twoUp = false, userKey = null, onEditorState, title: titleOverride,
  presentation = null, hidden = false, onStateChange, onViewAll, viewerHost = null, portalHost = null, mediaClient = PROJECT_ADAPTER }) {
  const office = mediaClient.entityType === "office";
  const entityLabel = mediaClient.entityType === "market" ? "market" : office ? "office" : "project";
  const drawer = presentation === "drawer";
  const preview = presentation === "preview";
  const [uploads, setUploads] = useState([]);
  const captured = capturedItems !== undefined;
  const [state, setState] = useState({ status: "loading", items: [], capabilities: {} });
  const [retry, setRetry] = useState(0);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const mounted = useRef(true);
  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);
  const [error, setError] = useState(null);
  const [edit, setEdit] = useState(null);
  const [caption, setCaption] = useState("");
  const [captionOriginal, setCaptionOriginal] = useState("");
  const captionDirty = edit != null && caption !== captionOriginal;
  const [leaveIntent, setLeaveIntent] = useState(null);
  const leaveRef = useRef(null);
  const uploadBatch = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [remove, setRemove] = useState(null);
  const removeTrigger = useRef(null);
  const [moveNotice, setMoveNotice] = useState("");
  const input = useRef(null);
  const generation = useRef(0);
  const sourceId = useRef(`media-${Math.random().toString(36).slice(2)}`);
  const [coverNotice, setCoverNotice] = useState(null);
  const editorCb = useRef(onEditorState);
  editorCb.current = onEditorState;
  const requestLeave = useCallback(proceed => leaveRef.current(proceed), []);
  leaveRef.current = proceed => {
    setRemove(null);
    if (busyRef.current || captionDirty) setLeaveIntent(old => old ?? { proceed, trigger: document.activeElement, waitForBusy: busyRef.current });
    else { setEdit(null); proceed(); }
  };
  useEffect(() => { editorCb.current?.({ editing: captionDirty, busy, uploading, transient: Boolean(remove), requestLeave }); }, [captionDirty, remove, busy, uploading, requestLeave]);
  useEffect(() => () => editorCb.current?.({ editing: false, busy: false }), []);
  useEffect(() => { if (hidden || presentation === "preview") setRemove(null); }, [hidden, presentation]);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      const batch = uploadBatch.current;
      if (batch) { batch.cancelled = true; batch.controller.abort(); }
      busyRef.current = false;
    };
  }, []);
  // One read sequence for the initial load, event refreshes and cover
  // re-reads; mutations bump it so an earlier read cannot undo a local change.
  const readSeq = useRef(0);
  const [refreshError, setRefreshError] = useState(null);
  useEffect(() => {
    let active = true;
    const current = ++generation.current;
    const mine = ++readSeq.current;
    setRefreshError(null);
    setBusy(false);
    setState({ status: "loading", items: [], capabilities: {} });
    setError(null); setEdit(null); setRemove(null); setUploads([]);
    if (captured) {
      // Archive: render exactly the captured references; never fetch live media.
      setState({ status: "ok", items: Array.isArray(capturedItems) ? capturedItems : [], capabilities: {} });
      return () => { active = false; generation.current = current + 1; };
    }
    Promise.resolve().then(() => mediaClient.list(projectId)).then(result => {
      if (active && mine === readSeq.current) setState({ ...result, status: "ok" });
    }).catch(err => { if (active && mine === readSeq.current) setState({ status: "error", items: [], capabilities: {}, error: err.message }); });
    return () => { active = false; generation.current = current + 1; };
  }, [projectId, retry, captured, capturedItems, mediaClient]);
  // Another instance (Updates/drawer) changed this project's media: refetch
  // in place without resetting open editors. Stale responses are dropped.
  useEffect(() => {
    if (captured) return undefined;
    return mediaClient.subscribe(projectId, detail => {
      if (detail?.source === sourceId.current) return;
      if (busyRef.current) { pendingRefresh.current = true; return; }
      refreshList();
    });
  }, [projectId, captured, mediaClient]); // eslint-disable-line react-hooks/exhaustive-deps
  // Field-preserving re-read: keeps open editors and the current list; a
  // failure shows an explicit retry instead of clearing the list.
  const pendingRefresh = useRef(false);
  function refreshList(only = null) {
    const current = generation.current;
    const mine = ++readSeq.current;
    Promise.resolve().then(() => mediaClient.list(projectId)).then(result => {
      if (current !== generation.current || mine !== readSeq.current) return;
      setRefreshError(null);
      setState(s => only ? { ...s, coverMediaId: result.coverMediaId, effectiveCover: result.effectiveCover } : { ...result, status: "ok" });
    }).catch(err => {
      if (current === generation.current && mine === readSeq.current) setRefreshError(err?.message || "Could not refresh project images.");
    });
  }
  const announce = result => mediaClient.emit(projectId, sourceId.current, result);
  const prefKey = twoUp ? mediaPrefsKey(userKey, projectId) : null;
  const [prefs, setPrefs] = useState(() => readMediaPrefs(prefKey, twoUp));
  const [starNotice, setStarNotice] = useState(null);
  useEffect(() => { setPrefs(readMediaPrefs(prefKey, twoUp)); setStarNotice(null); }, [prefKey, twoUp]);
  const imageIds = useMemo(() => new Set(state.items.filter(item => item.type === "image").map(item => item.id)), [state.items]);
  // Stale/deleted IDs are excluded before ranking so they never hold a slot.
  const stars = useMemo(() => prefs.stars.filter(id => imageIds.has(id)).slice(0, MAX_MEDIA_STARS), [prefs.stars, imageIds]);
  const updatePrefs = useCallback(next => { setPrefs(next); writeMediaPrefs(prefKey, next); }, [prefKey]);
  const toggleStar = id => {
    if (stars.includes(id)) { setStarNotice(null); updatePrefs({ ...prefs, stars: stars.filter(x => x !== id) }); return; }
    if (stars.length >= MAX_MEDIA_STARS) { setStarNotice(`Up to ${MAX_MEDIA_STARS} images can be starred. Unstar one to pin another.`); return; }
    setStarNotice(null);
    updatePrefs({ ...prefs, stars: [...stars, id] });
  };
    const canManage = !captured && allowManage && state.status === "ok" && state.capabilities.canManage;
  const run = async action => {
    if (busyRef.current || !mounted.current) return false;
    busyRef.current = true;
    readSeq.current += 1; // invalidate in-flight reads
    const current = generation.current;
    setBusy(true); setError(null);
    try { await action(current); return mounted.current && current === generation.current; }
    catch (err) {
      if (current === generation.current) {
        setError(err.message);
        // Removal may revoke metadata successfully yet report retryable
        // physical-cleanup failure. Re-read even on error, without claiming
        // success or abandoning the editor, so a removed cover cannot linger.
        pendingRefresh.current = true;
        announce();
      }
      return false;
    }
    finally {
      busyRef.current = false;
      if (current === generation.current) {
        setBusy(false);
        if (pendingRefresh.current) { pendingRefresh.current = false; refreshList(); }
      }
    }
  };
  const patchUpload = (key, patch) => setUploads(list => list.map(u => u.key === key ? { ...u, ...patch } : u));
  const cancelUploads = () => {
    const batch = uploadBatch.current;
    if (!batch) return;
    batch.cancelled = true;
    batch.controller.abort();
    setUploads(list => list.map(u => ["queued", "uploading"].includes(u.status) ? { ...u, status: "cancelled" } : u));
    // Aborting the browser request cannot undo a server commit. Always read
    // authoritative metadata again; never compensate by deleting an upload.
    pendingRefresh.current = true;
  };
  // Drawer/preview path: per-file queue with genuine progress from the client.
  const uploadFilesQueued = files => {
    if (!canManage || busyRef.current || !files.length) return;
    const stamp = Date.now().toString(36);
    const batch = { controller: new AbortController(), cancelled: false };
    uploadBatch.current = batch;
    setUploading(true);
    const entries = files.map((file, i) => ({ key: `${stamp}-${i}`, file, name: file.name, status: "queued", loaded: 0, total: file.size, lengthComputable: false, error: null }));
    setUploads(list => [...list.filter(u => u.status === "queued" || u.status === "uploading"), ...entries.map(({ file, ...rest }) => rest)]);
    run(async current => {
      let ok = 0;
      for (const entry of entries) {
        if (current !== generation.current || batch.cancelled) break;
        const rejection = office && (/\.(heic|heif)$/i.test(entry.file.name) || /^image\/hei[cf]/i.test(entry.file.type))
          ? "HEIC isn't supported yet. Export as JPEG or PNG and try again." : uploadRejection(entry.file);
        if (rejection) { patchUpload(entry.key, { status: "error", error: rejection }); continue; }
        patchUpload(entry.key, { status: "uploading" });
        try {
          // Settle even adapters/test doubles which deliver a late success
          // after cancellation; the original promise remains observed.
          const signal = batch.controller.signal;
          let aborted;
          const cancellation = new Promise((_, reject) => {
            aborted = () => reject(new DOMException("Upload cancelled", "AbortError"));
            signal.addEventListener("abort", aborted, { once: true });
          });
          let item;
          try { item = await Promise.race([mediaClient.upload(projectId, entry.file, weekDate, progress => {
            if (current !== generation.current || batch.cancelled || !progress) return;
            patchUpload(entry.key, { loaded: progress.loaded ?? 0, total: progress.total ?? 0, lengthComputable: Boolean(progress.lengthComputable) });
          }, signal), cancellation]); }
          finally { signal.removeEventListener("abort", aborted); }
          if (current !== generation.current || batch.cancelled) break;
          ok++;
          patchUpload(entry.key, { status: "completed" });
          setState(s => ({ ...s, items: [...s.items, item] }));
        } catch (err) {
          if (current !== generation.current || batch.cancelled) break;
          const message = err?.message || "Upload failed.";
          patchUpload(entry.key, { status: "error", error: message });
        }
      }
      if (current !== generation.current) return;
      uploadBatch.current = null;
      setUploading(false);
      if (ok) { announce(); refreshCover(current); }
    });
  };
  // Single uploader for every presentation (Updates, legacy, drawer, preview).
  const uploadFiles = files => uploadFilesQueued(files);
  const upload = event => {
    const files = [...(event.target.files || [])];
    // Allow selecting the same file again after a rejected/storage-failed
    // upload; otherwise browsers suppress the next input change event.
    event.target.value = "";
    uploadFiles(files);
  };
  // Upload/remove can change the server-selected effective cover.
  const refreshCover = current => { if (current === generation.current) refreshList(true); };
  const coverId = state.coverMediaId ?? null;
  const isCover = item => coverId != null && String(coverId) === String(item.id);
  const changeCover = item => { setRemove(null); return run(async current => {
    const next = item ? item.id : null;
    const result = await mediaClient.setCover(projectId, next, weekDate);
    if (current !== generation.current) return;
    setState(s => ({ ...s, coverMediaId: result?.coverMediaId !== undefined ? result.coverMediaId : next, effectiveCover: result?.effectiveCover !== undefined ? result.effectiveCover : s.effectiveCover }));
    setCoverNotice(item ? `"${displayCaption(item)}" is now the cover image.` : "Cover selection cleared. The header now uses the first image automatically.");
    announce(result);
  }); };
  const coverButton = item => item.type === "image" && (isCover(item)
    ? <button type="button" style={btn} disabled={busy} data-testid={`media-clear-cover-${item.id}`} onClick={() => changeCover(null)} aria-label={`Clear cover selection (${displayCaption(item)}); the first image is used automatically`}>Clear cover</button>
    : <button type="button" style={btn} disabled={busy} data-testid={`media-set-cover-${item.id}`} onClick={() => changeCover(item)} aria-label={`Set ${displayCaption(item)} as cover`}>Set as cover</button>);
  const actions = item => canManage && <div style={{ marginTop: 8 }}>
    {coverButton(item)}{item.type === "image" && " "}
    <button type="button" style={btn} disabled={busy} onClick={() => startCaption(item)} aria-label={`Edit caption ${displayCaption(item)}`}>Caption</button>{" "}
    <button type="button" style={btn} disabled={busy} onClick={event => startRemove(item, event.currentTarget)} aria-label={`Remove ${displayCaption(item)}`}>Remove</button>
  </div>;
  // Updates reader is tiles-only; image actions live in each tile's menu.
  const startCaption = item => requestLeave(() => { setEdit(item.id); setCaption(item.caption ?? ""); setCaptionOriginal(item.caption ?? ""); setRemove(null); setError(null); });
  const startRemove = (item, trigger = document.activeElement) => requestLeave(() => { removeTrigger.current = trigger; setRemove(item); setEdit(null); setError(null); });
  const saveCaption = () => run(async current => {
    const item = await mediaClient.update(projectId, edit, caption, weekDate);
    if (current === generation.current) { setState(s => ({ ...s, items: s.items.map(old => old.id === item.id ? item : old) })); setEdit(null); announce(); }
  });
  const continueLeave = () => {
    if (!mounted.current) return;
    const intent = leaveIntent;
    setLeaveIntent(null); setEdit(null);
    if (intent) proceedConfirmedNavigation(intent.proceed);
  };
  const saveAndLeave = async () => { if (await saveCaption()) continueLeave(); };
  const cancelAndLeave = () => {
    cancelUploads();
    // The request's abort event settles the shared serializer before leaving.
    setLeaveIntent(intent => intent ? { ...intent, cancelUpload: true } : null);
  };
  useEffect(() => {
    if (leaveIntent?.cancelUpload && !busy) {
      if (captionDirty) setLeaveIntent(intent => ({ ...intent, cancelUpload: false }));
      else continueLeave();
    } else if (leaveIntent?.waitForBusy && !busy && !captionDirty && !error) {
      continueLeave();
    }
  }, [leaveIntent, busy, captionDirty, error]); // eslint-disable-line react-hooks/exhaustive-deps
  const leavePrompt = <MediaLeavePrompt intent={leaveIntent} busy={busy} uploading={uploading} dirty={captionDirty} error={error}
    onStay={() => setLeaveIntent(null)} onSave={saveAndLeave} onDiscard={continueLeave} onCancelUpload={cancelAndLeave} onContinue={continueLeave} />;
  const menuActions = item => canManage ? [
    ...(item.type === "image" ? [isCover(item)
      ? { key: "clear-cover", label: "Clear cover (use first)", disabled: busy, onSelect: () => changeCover(null) }
      : { key: "set-cover", label: "Set as cover", disabled: busy, onSelect: () => changeCover(item) }] : []),
    { key: "caption", label: "Caption", disabled: busy, onSelect: () => startCaption(item) },
    { key: "remove", label: "Remove", danger: true, disabled: busy, onSelect: () => startRemove(item) },
  ] : [];
  const uploadTile = twoUp && canManage ? <button type="button" data-testid="media-upload-tile" disabled={busy} onClick={() => input.current?.click()} style={{ minHeight: 68, width: "100%", marginTop: 8, border: `1.5px dashed ${T.blue}`, borderRadius: 10, background: T.blueSoft, color: T.blue, fontSize: T.fsSm, fontWeight: T.fwBold, cursor: busy ? "wait" : "pointer", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2 }}>
    <span>{busy ? "Uploading… Please wait." : "Drag & drop images or PDFs here, or click to upload."}</span>
  </button> : null;
  const title = titleOverride || (twoUp ? "Project images" : "Floor plans & photos");
  const cover = useMemo(() => resolveCover(state), [state]);
  // Captured evidence keeps its historical sequence; live readers share order.
  const orderedItems = useMemo(() => captured ? state.items : orderMedia(state.items, cover.id), [captured,state.items,cover.id]);
  const reorder = (ids, movedId, focus) => run(async current => {
    await mediaClient.reorder(projectId,ids,weekDate);
    if (current !== generation.current) return;
    setState(s => ({ ...s, items: ids.map((id,sortOrder) => ({ ...s.items.find(i => String(i.id) === id), sortOrder })) }));
    const message = `Moved ${displayCaption(state.items.find(i => String(i.id) === String(movedId)))} to position ${ids.indexOf(String(movedId))+1} of ${ids.length}.`;
    setMoveNotice(message);
    announce(); refreshList();
    focus?.();
  });
  const confirmRemoval = () => {
    const removed = remove;
    setRemove(null);
    run(async current => {
      const result = await mediaClient.remove(projectId,removed.id,weekDate);
      if (current !== generation.current) return;
      setState(s => ({ ...s, items:s.items.filter(i => i.id !== removed.id) }));
      setRemove(null); announce(); refreshList();
      showToast(removed.type === "pdf" ? "Document removed" : "Photo removed", {
        type:"success",duration:8000,action: { label:"Undo", onClick: () => {
          // Undo is an explicit, exact-owner action whose existing eight-second
          // window survives closing the gallery. Never update a new owner's UI.
          if (!mounted.current) {
            Promise.resolve().then(() => mediaClient.restore(projectId, removed.id, result.undoToken, weekDate))
              .then(() => announce())
              .catch(err => showToast(err.message || "Could not restore the attachment.", { type: "error" }));
            return;
          }
          if (busyRef.current) { showToast("Another gallery change is in progress. Undo was not applied.",{type:"error"}); return; }
          run(async now => {
            try {
              await mediaClient.restore(projectId,removed.id,result.undoToken,weekDate);
              if (now !== generation.current) return;
              announce(); refreshList();
            } catch (err) { showToast(err.message || "Could not restore the attachment.",{type:"error"}); throw err; }
          });
        } },
      });
    });
  };
  const removalDialog = canManage && remove && !hidden ? <MediaRemoveConfirmation item={remove} trigger={removeTrigger.current} onCancel={() => setRemove(null)} onConfirm={confirmRemoval} /> : null;
  const stateCb = useRef(onStateChange);
  stateCb.current = onStateChange;
  const canManageNow = Boolean(canManage);
  const editing = captionDirty;
  useEffect(() => {
    stateCb.current?.({ status: state.status, items: state.items, count: state.status === "ok" ? state.items.length : null, capabilities: state.capabilities, canManage: canManageNow, coverMediaId: state.coverMediaId ?? null, effectiveCover: state.effectiveCover ?? null, cover, error: state.error ?? null, busy, editing, uploads });
  }, [state, cover, canManageNow, busy, editing, uploads]);
  const fileDrag = event => !isGalleryDrag(event) && [...(event.dataTransfer?.types || [])].includes("Files");
  const dropFiles = event => {
    event.preventDefault(); dragDepth.current = 0; setDragging(false);
    const internal = isGalleryDrag(event);
    endGalleryDrag();
    if (!internal) uploadFiles([...(event.dataTransfer?.files || [])]);
  };
  if (drawer || preview) {
    const dropOn = drawer && canManage;
    const body = <section aria-label={entityLabel === "market" ? "Market media" : office ? "Office media" : "Project media"} data-testid={`${entityLabel}-media-${projectId}`} data-presentation={presentation} data-dragging={dragging || undefined} aria-busy={busy} hidden={hidden || undefined}
      onDragStartCapture={markGalleryDrag} onDragEndCapture={endGalleryDrag}
      onDragEnter={dropOn ? event => { if (fileDrag(event)) { event.preventDefault(); dragDepth.current++; setDragging(true); } } : undefined}
      onDragOver={dropOn ? event => { if (fileDrag(event)) { event.preventDefault(); event.dataTransfer.dropEffect = busy ? "none" : "copy"; } } : undefined}
      onDragLeave={dropOn ? event => { event.preventDefault(); if (--dragDepth.current <= 0) { dragDepth.current = 0; setDragging(false); } } : undefined}
      onDrop={dropOn ? dropFiles : undefined}
      style={{ display: hidden ? "none" : undefined }}>
      {canManage && <input ref={input} type="file" multiple aria-label={`Upload ${entityLabel} media`} accept=".png,.jpg,.jpeg,.webp,.pdf,image/png,image/jpeg,image/webp,application/pdf" disabled={busy} onChange={upload} style={{ display: "none" }} />}
        {preview && <MediaPreviewRow status={state.status} error={state.error} items={orderedItems} cover={cover} canManage={Boolean(canManage)} onViewAll={() => onViewAll?.()} onRetry={() => setRetry(n => n + 1)} />}
      {drawer && <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: "14px 16px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: T.fsXs, letterSpacing: ".08em", textTransform: "uppercase", color: T.dark }}>Media</h3>
          {state.status === "ok" && <span data-testid="media-count" style={{ fontSize: T.fsSm, color: T.mid }}>{state.items.length} {state.items.length === 1 ? "file" : "files"} · images, plans and PDFs</span>}
        </div>
        {canManage && <button type="button" data-testid="media-drop-zone" disabled={busy} onClick={() => input.current?.click()}
          style={{ width: "100%", border: `1.5px dashed ${dragging ? T.blue : T.blueSoftBorder}`, borderRadius: 12, background: dragging ? T.blueSoft : T.blueSoft, padding: 16, color: T.blue, fontWeight: T.fwBold, fontSize: T.fsSm, cursor: busy ? "wait" : "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
          <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}><Icon name="upload" />{dragging ? "Drop to upload" : busy ? "Uploading. Please wait." : "Drag & drop images or PDFs here, or click to upload"}</span>
          <small style={{ color: T.mid, fontWeight: T.fwSemibold }}>PNG, JPEG, WebP or PDF · up to 20 MB each{entityLabel === "project" && " · shared with the Updates pane"}</small>
        </button>}
        <UploadQueue uploads={uploads} onCancel={cancelUploads} onDismiss={key => setUploads(list => list.filter(u => u.key !== key))} />
        {busy && <p role="status">{uploading ? "Upload in progress: wait or cancel." : "Media change is saving. Please wait."}</p>}
        {state.status === "loading" && <div aria-busy="true" style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12, marginTop: 12 }}>{[0, 1, 2].map(i => <div key={i} style={{ aspectRatio: "1 / 1", borderRadius: 10, background: T.lightGray }} />)}<span role="status" style={{ gridColumn: "1 / -1", ...muted }}>Loading {entityLabel} media…</span></div>}
        {state.status === "error" && <div role="alert" style={{ color: T.red, marginTop: 12 }}>Couldn't load {entityLabel} media: {state.error} <button type="button" style={btn} onClick={() => setRetry(n => n + 1)}>Retry</button></div>}
        {state.status === "ok" && state.items.length === 0 && <p data-testid="media-empty" style={{ textAlign: "center", color: T.mid, padding: "28px 0 8px", margin: 0 }}>No media yet. Add photos, renderings, floor plans or PDFs to tell this {entityLabel}'s story.</p>}
        {state.status === "ok" && state.items.length > 0 && <MediaDrawerGrid items={orderedItems} cover={cover} canManage={Boolean(canManage)} busy={busy} hidden={hidden} viewerHost={viewerHost} onReorder={reorder}
          onSetCover={item => changeCover(item)} onClearCover={() => changeCover(null)} onCaption={startCaption} onRemove={startRemove} />}
      </div>}
      {drawer && canManage && edit && <form style={{ marginTop: 10 }} onSubmit={event => { event.preventDefault(); saveCaption(); }}>
        <label style={{ fontSize: T.fsSm }}>Caption<input autoFocus style={inputStyle} aria-label="Media caption" maxLength={500} value={caption} disabled={busy} onChange={event => setCaption(event.target.value)} /></label>
        <button type="submit" style={btn} disabled={busy || !caption.trim()}>Save caption</button>{" "}
        <button type="button" style={btn} disabled={busy} onClick={() => setEdit(null)}>Cancel</button>
      </form>}
      {drawer && removalDialog}
      {leavePrompt}
      <span role={moveNotice ? "status" : undefined} aria-live="polite">{moveNotice}</span>
      {drawer && coverNotice && <div role="status" data-testid="media-cover-notice" style={{ ...muted, fontSize: T.fsXs, marginTop: 8 }}>{coverNotice}</div>}
      {refreshError && <div role="alert" data-testid="media-refresh-error" style={{ color: T.red, fontSize: T.fsSm, marginTop: 8 }}>Couldn't refresh {entityLabel} images: {refreshError} <button type="button" style={btn} data-testid="media-refresh-retry" onClick={() => { setRefreshError(null); refreshList(); }}>Retry</button></div>}
      {drawer && error && !remove && <div role="alert" style={{ color: T.red, fontSize: T.fsSm, marginTop: 8, whiteSpace: "pre-line" }}>{error}</div>}
    </section>;
    return portalHost ? createPortal(body, portalHost) : body;
  }
  return <section aria-label={title} data-testid={`project-media-${projectId}`} data-dragging={dragging || undefined} aria-busy={busy}
    onDragStartCapture={markGalleryDrag} onDragEndCapture={endGalleryDrag}
    onDragEnter={twoUp && canManage ? event => { if (fileDrag(event)) { event.preventDefault(); dragDepth.current++; setDragging(true); } } : undefined}
    onDragOver={twoUp && canManage ? event => { if (fileDrag(event)) { event.preventDefault(); event.dataTransfer.dropEffect = busy ? "none" : "copy"; } } : undefined}
    onDragLeave={twoUp && canManage ? event => { event.preventDefault(); if (--dragDepth.current <= 0) { dragDepth.current = 0; setDragging(false); } } : undefined}
    onDrop={canManage ? dropFiles : undefined}
    style={{ marginTop: twoUp ? 0 : 16, outline: dragging ? `2px dashed ${T.blue}` : undefined, outlineOffset: 5, background: dragging ? T.blueSoft : undefined }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 8 }}>
      <h3 style={{ fontSize: twoUp ? T.fsBase : T.fsSm, ...(twoUp ? { fontWeight: T.fwSemibold } : {}), color: T.dark, margin: 0 }}>{title}{twoUp && state.status === "ok" && <span data-testid="media-count" style={{ marginLeft: 8, fontSize: T.fsXs, color: T.mid, fontWeight: T.fwSemibold }}>{state.items.length} {state.items.length === 1 ? "attachment" : "attachments"}</span>}</h3>
      {canManage && !twoUp && <button type="button" style={btn} disabled={busy} onClick={() => input.current?.click()}>{busy ? "Saving…" : "Add photo"}</button>}
    </div>
    {canManage && <><input ref={input} type="file" multiple aria-label="Upload project media" accept=".png,.jpg,.jpeg,.webp,.pdf" disabled={busy} onChange={upload} style={{ display: "none" }} /><p style={{ ...muted, fontSize: T.fsXs }}>PNG, JPEG, WebP or PDF · up to 20 MB per file</p></>}
    {state.status === "loading" && <p role="status" style={muted}>Loading project media…</p>}
    {state.status === "error" && <div role="alert" style={{ color: T.red }}>{state.error} <button type="button" style={btn} onClick={() => setRetry(n => n + 1)}>Retry</button></div>}
    {state.status === "ok" && <MediaStrip items={orderedItems} canReorder={Boolean(canManage)} busy={busy} onReorder={reorder} renderActions={twoUp ? item => item.type === "image" ? null : actions(item) : actions} menuActions={twoUp ? menuActions : null} twoUp={twoUp} layout={twoUp ? "tile" : null} coverId={captured ? null : cover.id} stars={twoUp ? stars : null} onToggleStar={twoUp ? toggleStar : null} hideKind={twoUp} emptyText={captured ? "No media was captured with this archive." : "No floor plans or photos attached to this project."} />}
    {uploadTile}
    <UploadQueue uploads={uploads} onCancel={cancelUploads} onDismiss={key => setUploads(list => list.filter(u => u.key !== key))} />
    {canManage && edit && <form onSubmit={event => { event.preventDefault(); saveCaption(); }}>
      <label>Caption<input autoFocus style={inputStyle} aria-label="Media caption" maxLength={500} value={caption} disabled={busy} onChange={event => setCaption(event.target.value)} /></label>
      <button type="submit" style={btn} disabled={busy || !caption.trim()}>Save caption</button>{" "}
      <button type="button" style={btn} disabled={busy} onClick={() => setEdit(null)}>Cancel</button>
    </form>}
    {removalDialog}
    {leavePrompt}
    <span role={moveNotice ? "status" : undefined} aria-live="polite">{moveNotice}</span>
    {coverNotice && <div role="status" data-testid="media-cover-notice" style={{ ...muted, fontSize: T.fsXs, marginTop: 8 }}>{coverNotice}</div>}
    {refreshError && <div role="alert" data-testid="media-refresh-error" style={{ color: T.red, fontSize: T.fsSm, marginTop: 8 }}>Couldn't refresh project images: {refreshError} <button type="button" style={btn} data-testid="media-refresh-retry" onClick={() => { setRefreshError(null); refreshList(); }}>Retry</button></div>}
    {starNotice && <div role="status" data-testid="media-star-limit" style={{ ...muted, fontSize: T.fsXs, marginTop: 8 }}>{starNotice}</div>}
    {error && !remove && <div role="alert" style={{ color: T.red, fontSize: T.fsSm, marginTop: 8, whiteSpace: "pre-line" }}>{error}</div>}
  </section>;
}
